import { getPresignedImageUrlForFile } from "@/network/network";
import { toast } from "sonner";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";

export async function copyImageToClipboard(file: SwivuFile): Promise<void> {
  if (!navigator.clipboard || !isImageFileTyped(file)) {
    toast.error("Unable to copy this file to the clipboard.");
    return;
  }

  try {
    const img = new Image();
    img.crossOrigin = "anonymous";

    const imageLoaded = new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
    });

    const response = await getPresignedImageUrlForFile(file.id);
    img.src = response.data.url;
    await imageLoaded;

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    ctx.drawImage(img, 0, 0);

    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/png");
    });

    if (!pngBlob) {
      throw new Error("Failed to convert canvas to blob");
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        "image/png": pngBlob,
      }),
    ]);

    toast.success("Image copied to clipboard!");
  } catch (error) {
    console.error("Error copying image:", error);
    toast.error("Unable to copy image to clipboard. Please try downloading instead.");
  }
}
